import { Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Component
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import Table from 'src/components/Table';
import { renderNumberOrDefault, renderValueNumberTrade } from 'src/utils/helpers/stringHelpers';

import useCreditFlow from 'src/hooks/useCreditFlow';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useQuerySupplierBuyerUnitPriceTable } from 'src/services/queries/useQueryTradeData';
import { TypeBuyerSupplier } from 'src/utils/constants/enum';
import { TypeQueryParamPortTransaction } from 'src/utils/typings/dto/tradeData';
import { useTradeDataSortStore } from 'src/zustand/useTradeDataSortStore';
import { BarChartPrimaryColor } from 'src/components/HighChart/Barchart/config';

import { get, isEmpty, isNumber, map } from 'lodash';
import LinkToCompanyDetail from 'src/components/LinkToCompanyDetail';
import CompanyInfoNoData from 'src/features/CompanyDetail/Common/CompanyInfoNoData';
import FilterChart from 'src/features/TradeData/Common/FilterChart';
import { TypeTabTradeData } from 'src/features/TradeData/config';
import { EmptyText } from 'src/utils/constants/config';
import { LIST_ACTIONS, LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import BarChart from 'src/components/HighChart/Barchart';
import FlagCountry from 'src/components/common/FlagCountry';
import getEmojiFlag from 'src/utils/helpers/emojiFlag';

const UnitPriceSupplierBuyer = ({ selectedTab }: { selectedTab: TypeTabTradeData }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryParams = useQueryParams();
  const { setSort, unitPriceSupplierSortParams, unitPriceBuyerSortParams } = useTradeDataSortStore();

  const [acceptCreditPayment, setAcceptCreditPayment] = useState(true);
  const [selectedRangeTime, setSelectedRangeTime] = useState({
    fromTime: queryParams.start_date,
    toTime: queryParams.end_date,
  } as {
    fromTime: string;
    toTime: string;
  });

  const isBuyer = selectedTab === TypeTabTradeData.UnitPriceBuyers;
  const tableSortParams = isBuyer ? unitPriceBuyerSortParams : unitPriceSupplierSortParams;
  const tableSortParamsName = isBuyer ? 'unitPriceBuyerSortParams' : 'unitPriceSupplierSortParams';

  const formatAvgPrice = (n: number | null | undefined) => (isNumber(n) ? Number(n).toFixed(2) : EmptyText);

  const [tablePaging, setTablePaging] = useState({
    page: 0,
    page_size: 10,
  });

  useEffect(() => {
    setTablePaging({
      page: 0,
      page_size: 10,
    });
  }, [selectedTab]);

  const chartRequestPayload: TypeQueryParamPortTransaction = useMemo(() => {
    return {
      ...queryParams,
      limit: 20,
      offset: 0,
      start_date: selectedRangeTime.fromTime,
      end_date: selectedRangeTime.toTime,
      sort_by: tableSortParams?.sortBy,
      order: tableSortParams?.sort,
      is_buyer: isBuyer,
    };
  }, [queryParams, isBuyer, selectedRangeTime, tableSortParams]);

  const tableRequestPayload: TypeQueryParamPortTransaction = useMemo(() => {
    return {
      ...chartRequestPayload,
      limit: tablePaging.page_size,
      offset: tablePaging.page,
    };
  }, [chartRequestPayload, tablePaging]);

  const {
    data: proportionalTableData,
    dataMapping: proportionalTableDataMapping,
    isFetching: proportionalTableIsFetching,
  } = useQuerySupplierBuyerUnitPriceTable(acceptCreditPayment ? tableRequestPayload : undefined);

  const {
    data: chartTableData,
    dataMapping: chartDataMapping,
    isFetching: isFetchingChart,
  } = useQuerySupplierBuyerUnitPriceTable(acceptCreditPayment ? chartRequestPayload : undefined);

  const chartData = getArrayDefault(chartDataMapping);

  const [shouldShowBillableDataLoading] = useCreditFlow({
    response: proportionalTableData,
    featureInfo: {
      featureId: LIST_FEATURES.INTERNAL_TRADE_DATA_UNIT_PRICES,
      action: LIST_ACTIONS.VIEW,
    },
    acceptCreditPayment,
    onSetAcceptCreditPayment: (value) => setAcceptCreditPayment(value),
  });

  const tableConfigColumn: ConfigCell<IBuyerSupplierUnitPrice>[] = [
    {
      disablePadding: false,
      disableSort: true,
      id: 'entity_id',
      label: isBuyer ? t('trade_data.buyers.table.buyers') : t('trade_data.buyers.table.suppliers'),
      cell: (data: IBuyerSupplierUnitPrice) => {
        return (
          <Stack direction="row" spacing={1}>
            <FlagCountry code={data?.country_code || ''} />
            <LinkToCompanyDetail
              id={data?.entity_id || ''}
              companyName={data?.entity_name || ''}
              params={queryParams}
              viewedAsType={isBuyer ? TypeBuyerSupplier.buyer : TypeBuyerSupplier.supplier}
            />
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'avg_unit_price',
      label: 'Avg Price',
      cell: (data: IBuyerSupplierUnitPrice) => (
        <Typography variant="body2" color="text.secondary">
          {formatAvgPrice(data.avg_unit_price)} ({proportionalTableData?.unit})
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'total_volume',
      label: 'Total Volume',
      cell: (data: IBuyerSupplierUnitPrice) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.total_volume)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'transactions',
      label: 'Sample Size',
      cell: (data: IBuyerSupplierUnitPrice) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.transactions)}
        </Typography>
      ),
    },
  ];

  const barChart = useMemo(() => {
    const XData = map(chartData, (transaction) => get(transaction, 'avg_unit_price') ?? 0);
    const xAxis = map(chartData, ({ entity_name, country_code }) => {
      return `${getEmojiFlag(country_code)} ${entity_name}`;
    });
    const unitData = chartTableData?.unit;
    return (
      <BarChart
        tooltipFormatter={function () {
          const point: Highcharts.Point = this.point;
          const row = chartData[point.index ?? 0];
          const companyName = row?.entity_name ?? point.category ?? '';
          const countryFlag = getEmojiFlag(row?.country_code ?? '');
          const value = formatAvgPrice(point.options.y);
          const unit = unitData ?? '';
          const sampleSize = row?.transactions != null ? renderNumberOrDefault(row.transactions) : EmptyText;
          return `<div style="font-size: 14px; font-weight: 500">
                  <span style="display: block; font-weight: 600; margin-bottom: 6px;">${countryFlag} ${companyName}</span><br/>Average price: ${value} ${unit}<br/>Sample size: ${sampleSize}
                </div>`;
        }}
        yAxis={{
          type: 'logarithmic',
          minorTickInterval: 'auto',
        }}
        xAxis={{
          categories: xAxis,
        }}
        series={[
          {
            data: XData,
            type: 'column',
            showInLegend: false,
            borderRadius: 8,
            color: BarChartPrimaryColor,
            dataLabels: {
              rotation: 0,
              verticalAlign: 'top',
              y: -20,
              align: 'left',
              enabled: true,
              color: theme.palette.text.primary,
              formatter: function () {
                return formatAvgPrice(this.point.y ?? 0);
              },
            },
          },
        ]}
        loading={isFetchingChart}
        height={600}
      />
    );
  }, [chartData, chartTableData?.unit, isFetchingChart, theme.palette.text.primary]);

  if (
    !proportionalTableIsFetching &&
    !isFetchingChart &&
    isEmpty(getArrayDefault(proportionalTableDataMapping)) &&
    isEmpty(chartData)
  ) {
    return (
      <CompanyInfoNoData
        title={t('trade_data.common.notice_no_data.title')}
        content={<>{t('trade_data.common.notice_no_data.sub_title')}</>}
      />
    );
  }

  return (
    <Stack direction="column" spacing={3}>
      <WhiteBoxContent>
        <Stack direction="column" spacing={3}>
          <FilterChart
            onSelectTimeRange={({ fromTime, toTime }) => {
              setSelectedRangeTime({
                fromTime: fromTime,
                toTime: toTime,
              });
            }}
            title={isBuyer ? t('trade_data.unit_price.buyers.chart') : t('trade_data.unit_price.suppliers.chart')}
            selectedRangeTime={{
              fromTime: chartRequestPayload.start_date || '',
              toTime: chartRequestPayload.end_date || '',
            }}
          />
          {barChart}
        </Stack>
      </WhiteBoxContent>
      <WhiteBoxContent>
        <Stack direction="column" spacing={3}>
          <Typography display={'flex'} justifyContent={'space-between'} textAlign="left" variant="h5">
            {isBuyer ? t('trade_data.buyers.table.title') : t('trade_data.suppliers.table.title')}
          </Typography>
          <Table
            maxRow={50}
            handleSort={(sortData) => {
              setSort(tableSortParamsName, sortData);
            }}
            onChangePage={(value) => {
              setTablePaging({ ...tablePaging, page: value });
            }}
            onChangePageSize={(value) => setTablePaging({ page: 0, page_size: value })}
            loading={shouldShowBillableDataLoading || proportionalTableIsFetching}
            page={proportionalTableData.page}
            pageSize={proportionalTableData.page_size}
            totalRecords={proportionalTableData.total_records}
            dataKey="entity_id"
            data={proportionalTableDataMapping}
            configColumn={tableConfigColumn}
          />
        </Stack>
      </WhiteBoxContent>
    </Stack>
  );
};

export default UnitPriceSupplierBuyer;
